// src/hooks/useServiceWorkerUpdate.ts

import { useState, useEffect, useCallback } from 'react';

interface UseServiceWorkerUpdateResult {
  updateAvailable: boolean;
  applyUpdate: () => void;
}

export function useServiceWorkerUpdate(): UseServiceWorkerUpdateResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.register('/sw.js').then((reg) => {

      // If there's already a waiting worker on load, surface the update
      if (reg.waiting && navigator.serviceWorker.controller) {
        setWaitingWorker(reg.waiting);
        setUpdateAvailable(true);
        return;
      }

      const handleUpdateFound = () => {
        const newWorker = reg.installing;
        if (!newWorker) return;

        const handleStateChange = () => {
          // New SW installed while an existing controller is active = update ready
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(newWorker);
            setUpdateAvailable(true);
          }
        };

        newWorker.addEventListener('statechange', handleStateChange);
      };

      reg.addEventListener('updatefound', handleUpdateFound);
    }).catch((err) => {
      console.warn('Service worker registration failed:', err);
    });

    return () => {
      // Cleanup is minimal since SW registration persists across component lifecycles
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) return;

    // Post SKIP_WAITING message to the waiting service worker
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });

    // Listen for controller change then reload
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
  }, [waitingWorker]);

  return { updateAvailable, applyUpdate };
}
